import { ArrowUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PageMeta from '../components/PageMeta';
import useReveal from '../hooks/useReveal';

const INSIGHT_SLUGS = ['the-swiss-idea-of-capital', 'the-anatolian-thesis', 'the-quiet-holding-company'];
const INSIGHT_IMAGES = {
  'the-swiss-idea-of-capital': 'https://images.unsplash.com/photo-1546846195-825303d86f02?crop=entropy&cs=srgb&fm=jpg&q=85',
  'the-anatolian-thesis': 'https://images.unsplash.com/photo-1703639948834-342fc34900f8?crop=entropy&cs=srgb&fm=jpg&q=85',
  'the-quiet-holding-company': 'https://images.unsplash.com/photo-1472803828399-39d4ac53c6e5?crop=entropy&cs=srgb&fm=jpg&q=85',
};

export default function Insights() {
  const { t } = useTranslation();
  useReveal();
  const [featuredSlug, ...restSlugs] = INSIGHT_SLUGS;

  return (
    <div data-testid="insights-page">
      <PageMeta pageKey="insights" />
      <section className="px-6 md:px-12 pt-16 md:pt-24 pb-16">
        <div className="max-w-7xl mx-auto">
          <p className="reveal text-[10px] tracking-[0.35em] uppercase text-[#B58F45] mb-8">{t('insights_page.eyebrow')}</p>
          <h1 className="reveal font-serif text-5xl md:text-7xl lg:text-8xl text-[#0B1F3F] leading-[1.02] tracking-tight max-w-5xl">
            {t('insights_page.title_pre')} <span className="italic">{t('insights_page.title_italic')}</span> {t('insights_page.title_post')}
          </h1>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-20">
        <div className="max-w-7xl mx-auto">
          <article
            data-testid={`insight-featured-${featuredSlug}`}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 group cursor-pointer border border-[#0B1F3F]/10 hover:border-[#0B1F3F]/35 transition-colors"
          >
            <div className="lg:col-span-7 aspect-[16/10] overflow-hidden border-b lg:border-b-0 lg:border-r border-[#0B1F3F]/10">
              <img src={INSIGHT_IMAGES[featuredSlug]} alt={t(`insights.${featuredSlug}.headline`)} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[900ms]" />
            </div>
            <div className="lg:col-span-5 p-8 md:p-12 flex flex-col justify-center">
              <div className="flex items-center gap-4 text-[10px] font-sans text-[#6B5B4A] mb-6 uppercase tracking-[0.25em]">
                <span className="text-[#B58F45]">{t(`insights.${featuredSlug}.category`)}</span>
                <span>·</span>
                <span>{t(`insights.${featuredSlug}.date`)}</span>
              </div>
              <h2 className="reveal font-serif text-3xl md:text-5xl text-[#0B1F3F] leading-tight group-hover:text-[#B58F45] transition-colors mb-6">
                {t(`insights.${featuredSlug}.headline`)}
              </h2>
              <p className="text-[#6B5B4A] leading-relaxed mb-8">{t(`insights.${featuredSlug}.excerpt`)}</p>
              <span className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#0B1F3F] group-hover:text-[#B58F45]">
                {t('insights_page.read_letter')} <ArrowUpRight size={14} />
              </span>
            </div>
          </article>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10">
          {restSlugs.map((slug) => (
            <article
              key={slug}
              data-testid={`insight-card-${slug}`}
              className="group cursor-pointer border border-[#0B1F3F]/10 bg-[#EBE3D0] hover:bg-[#8B6F47] transition-colors"
            >
              <div className="aspect-[16/9] overflow-hidden border-b border-[#0B1F3F]/10">
                <img src={INSIGHT_IMAGES[slug]} alt={t(`insights.${slug}.headline`)} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[900ms]" />
              </div>
              <div className="p-8 md:p-10">
                <div className="flex items-center gap-4 text-[10px] font-sans text-[#6B5B4A] mb-4 uppercase tracking-[0.25em]">
                  <span className="text-[#B58F45]">{t(`insights.${slug}.category`)}</span>
                  <span>·</span>
                  <span>{t(`insights.${slug}.date`)}</span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-[#0B1F3F] leading-snug group-hover:text-[#B58F45] transition-colors mb-4">
                  {t(`insights.${slug}.headline`)}
                </h3>
                <p className="text-[#6B5B4A] leading-relaxed">{t(`insights.${slug}.excerpt`)}</p>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
